import React, { useEffect, useRef, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import {
  getSession, clearSession, fetchData, getReceivedAt, postAction, deleteAction,
  uploadData, downloadActions, fmtDay, fmtDates,
  validateSession, saveSession, fetchRecv, googleSignIn, logout,
} from "../lib/vikas";
import { Gate } from "../components/Gate";
import { WhatChanged } from "../components/WhatChanged";
import { Brand } from "../components/Brand";
import { Drawer } from "../components/panels";
import { MapBoard } from "../components/map";
import { Board } from "../components/board/Board";
import { VikasBubble } from "../components/board/VikasBubble";
import { VikasChat } from "../components/board/VikasChat";
import UsersPage from "../components/board/Users";
import { startBeacon, beacon, tap } from "../lib/beacon";
import { PRIVATE_FRAMES } from "../components/board/PrivateFrame";
import { panelOf } from "../components/board/model";

// follows the device setting until the Light/Dark button picks one (remembered)
function useTheme() {
  const mq = window.matchMedia("(prefers-color-scheme: light)");
  const [pick, setPick] = useState(() => localStorage.getItem("vikas_theme"));
  const [device, setDevice] = useState(() => (mq.matches ? "light" : "dark"));
  useEffect(() => {
    const on = (e) => setDevice(e.matches ? "light" : "dark");
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [mq]);
  const theme = pick || device;
  useEffect(() => { document.documentElement.classList.toggle("light", theme === "light"); }, [theme]);
  const setTheme = (t) => { localStorage.setItem("vikas_theme", t); setPick(t); };
  return [theme, setTheme];
}

function nowIST() {
  return new Date().toLocaleString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
}

const OWN_MOVES = ["close", "new date", "note", "stale", "not relevant", "vikas mistake"];
const isOption = (c, action) => Array.isArray(c?.options)
  && c.options.some((o) => String(o).trim().toLowerCase() === String(action || "").trim().toLowerCase());

export default function Dashboard() {
  const [session, setSession] = useState(() => getSession());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useTheme();
  const [chat, setChat] = useState({ open: false, card: "" });
  const [drawer, setDrawer] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [queued, setQueued] = useState({});
  const [recv, setRecv] = useState({});   // server-persisted "received" badges
  const [queue, setQueue] = useState([]);
  const [queueOpen, setQueueOpen] = useState(false);
  const [received, setReceived] = useState("");   // when the feed's last upload reached the board
  const [, setTick] = useState(0);                // re-render for "N min ago"
  const builtRef = useRef(null);
  const fileRef = useRef(null);
  const [route, setRoute] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const nav = useCallback((path) => {
    if (path !== window.location.pathname) window.history.pushState({}, "", path);
    setRoute(path);
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const t0 = performance.now();
    try {
      const d = await fetchData();
      const ms = performance.now() - t0;
      if (ms > 3000) beacon("slow", "board", { ms });
      setReceived(getReceivedAt());
      const built = d?.meta?.built_at || "";
      if (built !== builtRef.current) {
        builtRef.current = built;
        setData(d);
        setQueued({});
      }
      setRecv(await fetchRecv());
    } catch (e) {
      beacon("error", "board", { reason: `data_http_${e?.response?.status || 0}` });
      if (e?.response?.status === 401) { clearSession(); setSession(null); }
      else if (!silent) toast.error("Could not read the data file");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { if (session) load(); }, [session, load]);
  useEffect(() => { if (session) startBeacon(); }, [session]);

  const [authErr, setAuthErr] = useState("");
  const [authBusy, setAuthBusy] = useState(() => window.location.hash.includes("session_id="));
  // returning from Google: exchange session_id once, then clean the address bar
  const exchanged = useRef(false);
  useEffect(() => {
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m || exchanged.current) return;
    exchanged.current = true;
    const back = sessionStorage.getItem("vikas_after_login") || "/";
    sessionStorage.removeItem("vikas_after_login");
    window.history.replaceState({}, "", back);
    setRoute(back);
    googleSignIn(decodeURIComponent(m[1]))
      .then((s) => { saveSession(s); setSession({ role: s.role, canAct: s.can_act }); })
      .catch((e) => { clearSession(); setSession(null); setAuthErr(e?.response?.data?.detail || "Google sign-in did not complete"); })
      .finally(() => setAuthBusy(false));
  }, []);

  // on first mount, confirm the httpOnly session cookie is still valid (12h window)
  useEffect(() => {
    if (window.location.hash.includes("session_id=")) return;
    validateSession().then((meInfo) => {
      if (!meInfo) { if (getSession()) setSession(null); return; }
      const curExp = Number(localStorage.getItem("vikas_exp") || 0) || undefined;
      saveSession({ role: meInfo.role, can_act: meInfo.can_act, exp: curExp });
      setSession((s) => (s && s.role === meInfo.role && s.canAct === meInfo.can_act ? s : { role: meInfo.role, canAct: meInfo.can_act }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // H: poll for a newer data file every 60s; tick every 30s for the "ago" label
  useEffect(() => {
    if (!session) return;
    const poll = setInterval(() => load(true), 60000);
    const tick = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [session, load]);

  const act = async (panel, action, target_id, value = "", group = "", okMsg = "") => {
    const section = panel === "users" ? "users" : PRIVATE_FRAMES.has(panel) ? panel : group || (String(target_id).startsWith("TEAM-") ? "team" : "board");
    tap(section, action, target_id);
    try {
      const res = await postAction({ panel, action, target_id, value, group });
      if (panel !== "users") {
        setQueued((q) => ({ ...q, [`${panel}:${target_id}`]: true }));
        setRecv((r) => ({ ...r, [`${panel}:${target_id}`]: { by: session.role, at: new Date().toISOString() } }));
      }
      setQueue((qq) => [...qq, { id: res.id, panel, action, target_id, value, group, by: session.role, test: !!res.test, at: `${fmtDay(new Date().toISOString().slice(0, 10))} · ${nowIST()}` }]);
      toast.success(okMsg || "Queued — VIKAS acts on it at the next run");
      return res.id;
    } catch (e) {
      const why = String(e?.response?.data?.detail || "");
      const e2 = data?.meta?.rights?.[session.role] || {};
      const lvl = String(((e2.topics && typeof e2.topics === "object" ? e2.topics : e2)[section]) || "").toLowerCase();
      const reason = why.startsWith("Read-only") ? "not_may_act" : why.startsWith("No edit right") ? (lvl === "view" ? "view_only" : "no_right")
        : why.startsWith("Bad target") ? "bad_target" : why.startsWith("Only its owner") ? "no_right" : "";
      if (reason) beacon("refused", section, { reason, action: action === "vikas mistake" ? "mistake" : action.replace(/\s+/g, "_"), card_id: target_id });
      toast.error(e?.response?.data?.detail || "Could not queue");
      return null;
    }
  };

  const undo = async (actId) => {
    try {
      await deleteAction(actId);
      setQueue((qq) => qq.filter((x) => x.id !== actId));
      const item = queue.find((x) => x.id === actId);
      setQueued((q) => {
        if (!item) return q;
        const nq = { ...q }; delete nq[`${item.panel}:${item.target_id}`]; return nq;
      });
      setRecv((r) => {
        if (!item) return r;
        const nr = { ...r }; delete nr[`${item.panel}:${item.target_id}`]; return nr;
      });
      toast.success("Undone");
    } catch (e) {
      toast.error("Could not undo");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { await uploadData(file); toast.success("Data file replaced"); builtRef.current = null; await load(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Upload failed"); }
    finally { e.target.value = ""; }
  };

  const lock = () => { logout(); clearSession(); setSession(null); setData(null); };

  if (!session) {
    return (<><Toaster position="top-center" theme={theme} /><Gate error={authErr} busy={authBusy} /></>);
  }

  const meta = data?.meta || {};
  const asOf = meta.as_of || "";

  const recvMs = received ? Date.parse(received) : NaN;
  const recvTxt = Number.isNaN(recvMs) ? "" : new Date(recvMs).toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "");
  const dataLate = !Number.isNaN(recvMs) && Date.now() - recvMs > 16 * 3600 * 1000;

  const chatter = Boolean(session.role);
  const userAdmin = ["Saket", "Eshan"].includes(session.role);
  const chatPress = (p) => {
    const c = data?.board?.cards?.[p.card_id];
    if (!c) { toast.error("That card is not on the board"); return; }
    act(panelOf(c), p.kind, p.card_id, "", c.topic || "");
  };
  const ctx = {
    canAct: session.canAct, act, asOf, role: session.role, theme,
    userAdmin,
    teamPress: (c) => Boolean(c?.team_only) && (["Saket", "Eshan"].includes(session.role) || c.owner === session.role || (data?.meta?.mine?.[session.role] || []).includes(c.id)),
    ownPress: (c, action) => (OWN_MOVES.includes(action) || isOption(c, action)) && (data?.meta?.mine?.[session.role] || []).includes(c?.id),
    canPress: (c, action) => {
      if (c?.team_only) return ["done", "new date"].includes(action) && (["Saket", "Eshan"].includes(session.role) || c.owner === session.role || (data?.meta?.mine?.[session.role] || []).includes(c.id));
      const own = (OWN_MOVES.includes(action) || isOption(c, action)) && (data?.meta?.mine?.[session.role] || []).includes(c?.id);
      if (own || !c?.topic) return true;
      const e = data?.meta?.rights?.[session.role] || {};
      const t = e.topics && typeof e.topics === "object" ? e.topics : e;
      return String(t[c.topic] || "").toLowerCase() === "edit";
    },
    canEdit: (topic) => {
      if (!topic) return true;
      const e = data?.meta?.rights?.[session.role] || {};
      const t = e.topics && typeof e.topics === "object" ? e.topics : e;
      return String(t[topic] || "").toLowerCase() === "edit";
    },
    askVikas: chatter ? (id) => setChat({ open: true, card: id }) : null,
    openDrawer: setDrawer, closeDrawer: () => setDrawer(null),
    isQueued: (panel, id) => !!queued[`${panel}:${id}`] || !!recv[`${panel}:${id}`],
    recvFor: (panel, id) => recv[`${panel}:${id}`] || (queued[`${panel}:${id}`] ? { by: session.role } : null),
    queue, undo,
    pressedFor: (panel, id) => recv[`${panel}:${id}`] || (queued[`${panel}:${id}`] ? { by: session.role } : null),
    pressFor: (id) => {
      const k = Object.keys(recv).find((x) => x.slice(x.indexOf(":") + 1) === id)
        || Object.keys(queued).find((x) => x.slice(x.indexOf(":") + 1) === id);
      return k ? (recv[k] || { by: session.role, at: new Date().toISOString() }) : null;
    },
  };
  const board = data?.board || null;
  const boardName = board?.name || "VIKAS Dashboard";
  const nameParts = /^VIKAS\b/.test(boardName) ? ["VIKAS", boardName.slice(5).trim()] : [null, boardName];

  return (
    <div className={`min-h-screen flex flex-col${board ? " vb" : ""}`}>
      <Toaster position="top-center" theme={theme} />
      {chatter && <VikasBubble open={chat.open} onToggle={() => setChat((x) => ({ ...x, open: !x.open }))} />}
      {chatter && (
        <VikasChat open={chat.open} cardId={chat.card} onClose={() => setChat((x) => ({ ...x, open: false }))}
          clearCard={() => setChat((x) => ({ ...x, card: "" }))} onCite={(id) => nav(`/card/${encodeURIComponent(id)}`)} onPress={chatPress} />
      )}
      <header className="hdr-bar px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 border-b" style={{ borderColor: "var(--line)" }}>
        <div className="hdr-l flex items-center gap-3 min-w-0">
          <div className="hdr-brand flex items-center gap-3 min-w-0">
            <span className="brand-clearspace inline-flex hdr-logo">
              <Brand name="renevik-solar" height={26} testid="logo-solar" alt="Renevik Solar" />
            </span>
            {board ? (
              <h1 className="brand4 hdr-title" data-testid="app-title" aria-label={boardName}>
                {nameParts[0] && <span className="wm">{nameParts[0]}</span>}<span className="nm-brand">{nameParts[1]}</span>
              </h1>
            ) : (
              <h1 className="head text-lg m-0 whitespace-nowrap hdr-title" data-testid="app-title">VIKAS Dashboard</h1>
            )}
            {board && (
              <nav className="ptabs" data-testid="page-tabs">
                <a href="/" className={route !== "/eshan" && route !== "/health" ? "on" : ""} data-testid="tab-dashboard" onClick={(e) => { e.preventDefault(); nav("/"); }}>Dashboard</a>
                <a href="/eshan" className={route === "/eshan" ? "on" : ""} data-testid="tab-eshan" onClick={(e) => { e.preventDefault(); nav("/eshan"); }}>Eshan's reviews</a>
                {userAdmin && <a href="/health" className={route === "/health" ? "on" : ""} data-testid="tab-health" onClick={(e) => { e.preventDefault(); nav("/health"); }}>System health</a>}
              </nav>
            )}
          </div>
          <div className="hdr-metawrap flex items-center gap-3 min-w-0">
            <span className="hdr-meta whitespace-nowrap" data-testid="meta-as-of">{meta.as_of ? fmtDay(meta.as_of) : "—"}</span>
            {meta.development_copy && <span className="dev-pill" data-testid="dev-pill">development copy</span>}
            {recvTxt && (
              <span className={`fresh ${dataLate ? "late" : "ok"}`} data-testid="data-freshness">
                <span className="fic" aria-hidden="true">{dataLate ? "!" : "✓"}</span>
                {dataLate ? `Data late · last received ${recvTxt}` : `Data fresh · received ${recvTxt}`}
              </span>
            )}
          </div>
        </div>
        <div className="hdr-r flex items-center gap-2 flex-shrink-0">
          {queue.length > 0 && (
            <button className="queue-pill" data-testid="queue-pill" onClick={() => setQueueOpen(true)}>Queue · {queue.length}</button>
          )}
          <span className="chip whitespace-nowrap" data-testid="role-chip">{session.role}{session.canAct ? "" : " · read-only"}</span>
          <button className="vk-arrow" data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="toggle theme">
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          <button className="vk-arrow" data-testid="lock-btn" onClick={lock}>Lock</button>
          {userAdmin && (
            <div className="menu-wrap">
              <button className="vk-arrow" data-testid="menu-btn" aria-label="menu" onClick={() => setMenuOpen(!menuOpen)}>⋯</button>
              {menuOpen && (
                <div className="menu-pop" data-testid="menu-pop" onMouseLeave={() => setMenuOpen(false)}>
                  <button className="menu-item" data-testid="menu-users" onClick={() => { setMenuOpen(false); nav("/users"); }}>Users</button>
                  {session.role === "Saket" && (
                    <>
                      <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onUpload} data-testid="upload-input" />
                      <button className="menu-item" data-testid="what-changed-btn-wrap" onClick={() => { setMenuOpen(false); setShowWhatChanged(true); }}>What changed</button>
                      <button className="menu-item" data-testid="replace-data-btn" onClick={() => fileRef.current?.click()}>Replace data file</button>
                      <button className="menu-item" data-testid="download-actions-btn" onClick={() => { setMenuOpen(false); downloadActions().catch(() => toast.error("Download failed")); }}>Download actions</button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      {session.role === "Saket" && showWhatChanged && (
        <div className="px-4 sm:px-6 lg:px-8 pt-3"><WhatChanged autoOpen onClose={() => setShowWhatChanged(false)} /></div>
      )}

      {board ? (
        <main className="flex-1 w-full" data-testid="board-v4">
          {userAdmin && route === "/users" ? <UsersPage data={data} ctx={ctx} /> : <Board data={data} ctx={ctx} route={route} nav={nav} />}
        </main>
      ) : (
      <main className="flex-1 w-full" style={{ maxWidth: 1360, margin: "0 auto", padding: "0 1rem 4rem" }}>
        {loading && !data ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <Brand name="renevik-symbol" height={32} testid="loading-symbol" alt="" />
            <div className="nm">Reading the data file…</div>
          </div>
        ) : data ? (<MapBoard data={data} ctx={ctx} />) : null}
      </main>
      )}

      <footer className="px-4 sm:px-6 lg:px-8 py-5 border-t flex items-center justify-center gap-4" style={{ borderColor: "var(--line)" }}>
        <Brand name="own-your-power" height={22} testid="logo-tagline" alt="Own your power" />
        <span className="lbl" data-testid="footer">Prepared by VIKAS, our proprietary intelligence system</span>
      </footer>

      {drawer && <Drawer onClose={() => setDrawer(null)}>{drawer}</Drawer>}

      {queueOpen && (
        <Drawer onClose={() => setQueueOpen(false)}>
          <div data-testid="queue-drawer">
            <div className="dr-head"><h3 className="dr-title" data-testid="drawer-title">Queue · {queue.length}</h3></div>
            <p className="dr-p nm">One row per action, written to the actions file. VIKAS acts at the next run.</p>
            {queue.slice().reverse().map((q) => (
              <div key={q.id} className="queue-row" data-testid={`queue-row-${q.id}`}>
                <div className="min-w-0">
                  <div className="queue-act">{q.action}{q.value ? ` · ${q.value}` : ""}{q.test ? <span className="test-tag">test</span> : null}</div>
                  <div className="rowline-meta">{q.id} · {q.target_id} · {q.by} · {q.at}{q.group ? ` · ${q.group}` : ""}</div>
                </div>
                <button className="vk-arrow" data-testid={`queue-undo-${q.id}`} onClick={() => undo(q.id)}>Undo</button>
              </div>
            ))}
            <button className="vk-btn w-full mt-3" data-testid="queue-download" onClick={() => downloadActions().catch(() => toast.error("Download failed"))}>Download actions</button>
          </div>
        </Drawer>
      )}
    </div>
  );
}
